/** Right road section of the LUDOKA game board - Celeste skin */

import type { RoadSectionProps } from '../../constants/types';
import { renderMultiplePositions } from '../basics/utils/utils';
import { RIGHT_CENTER_SQUARE_BOUNDS as CENTER_SQUARE_BOUNDS } from '../basics/constants/center-square-bounds';
import type { BoardPosition } from '../../constants/positions';

/**
 * Render order for right section (Red)
 * Ordered right to left for proper 3D overlap
 */
const RENDER_ORDER: BoardPosition[] = [
  'RF1',
  'RC1',
  'RS1',
  'RF2',
  'RC2',
  'RS2',
  'RF3',
  'RC3',
  'RS3',
  'RF4',
  'RC4',
  'RS4',
  'RF5',
  'RC5',
  'RS5',
  'RF6',
  'RC6',
  'RS6',
  'RFINAL'
];

const SCALE_MULTIPLIER = 1.5; // Horizontal section needs larger pawns

export default function CelesteRightRoadSection({
  viewBox = '0 0 616 268',
  className = '',
  pawns = [],
  animatingPawnIds,
  kickingPawnIds,
  rollbackPawnIds,
  onPawnClick,
  clickablePawnIds,
  pawnComponents
}: RoadSectionProps) {
  return (
    <svg
      viewBox={viewBox}
      className={className}
      overflow="visible"
      xmlns="http://www.w3.org/2000/svg"
      aria-label="Right road section with red final stretch"
    >
      {/* Top right corner - sky-blue placeholder (no stroke) */}
      <path
        d="M587.71 88.58L510.87 88.58V11.74L577.71 11.74C583.23 11.74 587.71 16.22 587.71 21.74V88.58Z"
        fill="#C4C4C4"
      />

      {/* Column 6: White center path square (top row) */}
      <path
        d="M448.43 159.08H496.48C500.931 159.08 504.54 155.471 504.54 151.02V102.97C504.54 98.5186 500.931 94.91 496.48 94.91H448.43C443.979 94.91 440.37 98.5186 440.37 102.97V151.02C440.37 155.471 443.979 159.08 448.43 159.08Z"
        fill="white"
      />

      {/* Middle center column - light gray */}
      <path d="M510.87 165.42H587.71V88.58L510.87 88.58V165.42Z" fill="#EFEFEF" />

      {/* Column 6: Red gradient center */}
      <path
        d="M434.03 165.42H510.87V88.58L434.03 88.58V165.42Z"
        fill="url(#crt_paint0)"
        stroke="black"
        strokeMiterlimit="10"
      />

      {/* Column 6: White center path square (center row) */}
      <path
        d="M448.43 159.08H496.48C500.931 159.08 504.54 155.471 504.54 151.02V102.97C504.54 98.5186 500.931 94.91 496.48 94.91H448.43C443.979 94.91 440.37 98.5186 440.37 102.97V151.02C440.37 155.471 443.979 159.08 448.43 159.08Z"
        fill="white"
      />

      {/* Column 6: White top path square */}
      <path
        d="M448.43 82.24H496.48C500.931 82.24 504.54 78.6314 504.54 74.18V26.13C504.54 21.6786 500.931 18.07 496.48 18.07H448.43C443.979 18.07 440.37 21.6786 440.37 26.13V74.18C440.37 78.6314 443.979 82.24 448.43 82.24Z"
        fill="white"
      />

      {/* Column 5: Dark gradient center */}
      <path
        d="M357.18 165.42H434.02V88.58L357.18 88.58V165.42Z"
        fill="url(#crt_paint1)"
        stroke="black"
        strokeMiterlimit="10"
      />

      {/* Column 5: White center path square */}
      <path
        d="M371.58 159.08H419.63C424.081 159.08 427.69 155.471 427.69 151.02V102.97C427.69 98.5186 424.081 94.91 419.63 94.91H371.58C367.129 94.91 363.52 98.5186 363.52 102.97V151.02C363.52 155.471 367.129 159.08 371.58 159.08Z"
        fill="white"
      />

      {/* Column 4: Red gradient center */}
      <path
        d="M280.34 165.42H357.18V88.58L280.34 88.58V165.42Z"
        fill="url(#crt_paint2)"
        stroke="black"
        strokeMiterlimit="10"
      />

      {/* Column 4: White center path square */}
      <path
        d="M294.74 159.08H342.79C347.242 159.08 350.85 155.471 350.85 151.02V102.97C350.85 98.5186 347.242 94.91 342.79 94.91H294.74C290.289 94.91 286.68 98.5186 286.68 102.97V151.02C286.68 155.471 290.289 159.08 294.74 159.08Z"
        fill="white"
      />

      {/* Column 3: Dark gradient center */}
      <path
        d="M203.51 165.42H280.35V88.58L203.51 88.58V165.42Z"
        fill="url(#crt_paint3)"
        stroke="black"
        strokeMiterlimit="10"
      />

      {/* Column 3: White center path square */}
      <path
        d="M217.9 159.08H265.95C270.401 159.08 274.01 155.471 274.01 151.02V102.97C274.01 98.5186 270.401 94.91 265.95 94.91H217.9C213.448 94.91 209.84 98.5186 209.84 102.97V151.02C209.84 155.471 213.448 159.08 217.9 159.08Z"
        fill="white"
      />

      {/* Column 2: Red gradient center */}
      <path
        d="M126.67 165.42H203.51V88.58L126.67 88.58L126.67 165.42Z"
        fill="url(#crt_paint4)"
        stroke="black"
        strokeMiterlimit="10"
      />

      {/* Ground border - dark gradient base layer */}
      <path
        d="M587.67 228.96V254.54C587.29 259 585.97 265.01 581.3 268.83C574.69 274.23 565.04 272.24 558.01 271.22C539.45 268.52 546.6 273.71 503.41 273.48C475.63 273.33 476.2 271.21 450.87 271.15C428 271.1 426.83 272.83 402.73 273.46C373.54 274.23 372.14 271.77 330.27 270.94C300.08 270.34 301.25 271.61 268.16 270.94C237.87 270.33 239.36 269.33 210.26 268.92C197.37 268.74 193.44 268.88 150.67 268.92C136.84 268.93 134.24 268.92 126.79 268.92L126.67 241.53L587.68 228.97L587.67 228.96Z"
        fill="url(#crt_paint5)"
      />

      {/* Ground overlay - gray gradient top layer */}
      <path
        d="M126.72 229.11V256.5C134.16 256.5 136.84 256.51 150.66 256.5H152.14C155.39 256.49 158.42 256.49 161.23 256.48C163.01 256.48 164.72 256.48 166.34 256.48C170.56 256.48 174.25 256.46 177.51 256.46C190 256.43 196.2 256.39 201.77 256.42C204.25 256.43 206.6 256.45 209.32 256.49C209.63 256.49 209.94 256.5 210.26 256.5C211.23 256.51 212.17 256.53 213.08 256.54C216.47 256.6 219.42 256.67 222.04 256.73C239.68 257.22 242.67 258 268.16 258.51C280.87 258.77 288.52 258.74 294.84 258.64C297.12 258.61 299.23 258.56 301.34 258.52C303.31 258.48 305.28 258.44 307.4 258.41C309.26 258.38 311.25 258.36 313.44 258.36C316.47 258.34 319.87 258.36 323.95 258.41C324.18 258.41 324.41 258.41 324.65 258.41C326.4 258.43 328.27 258.46 330.27 258.5C330.45 258.5 330.63 258.5 330.81 258.5C331.68 258.52 332.54 258.54 333.38 258.56C372.23 259.45 374.29 261.76 402.73 261.02C415.87 260.67 422.19 260 428.93 259.47C432.53 259.19 436.25 258.94 441.19 258.81C441.44 258.8 441.69 258.8 441.94 258.79C444.53 258.72 447.46 258.7 450.87 258.71C453.26 258.71 455.42 258.73 457.39 258.77C472.31 259.06 476.67 260.22 490.11 260.76H490.13C493.05 260.87 496.41 260.97 500.39 261.01C501.29 261.03 502.22 261.03 503.18 261.04H503.41C531.85 261.18 538.46 258.99 544.95 258.24C547.1 258 549.23 257.91 552.14 258.12C553.42 258.2 554.85 258.34 556.5 258.56C556.98 258.62 557.49 258.69 558.01 258.77C560.55 259.14 563.42 259.64 566.37 259.86C571.6 260.27 577.07 259.84 581.3 256.39C584.59 253.7 586.22 249.92 587.03 246.36C587.37 244.87 587.56 243.42 587.67 242.1V216.52L397.46 221.7L357.66 222.78L293.01 224.54L270.47 225.16H270.46L236.54 226.08H236.32H236.31L169.98 227.88L132.23 228.91L126.72 229.08V229.11Z"
        fill="url(#crt_paint6)"
      />

      {/* Column 1: White center path squares */}
      <path
        d="M141.06 159.08H189.11C193.561 159.08 197.17 155.471 197.17 151.02V102.97C197.17 98.5186 193.561 94.91 189.11 94.91H141.06C136.609 94.91 133 98.5186 133 102.97V151.02C133 155.471 136.609 159.08 141.06 159.08Z"
        fill="white"
      />
      <path
        d="M141.06 159.08H189.11C193.561 159.08 197.17 155.471 197.17 151.02V102.97C197.17 98.5186 193.561 94.91 189.11 94.91H141.06C136.609 94.91 133 98.5186 133 102.97V151.02C133 155.471 136.609 159.08 141.06 159.08Z"
        fill="white"
      />

      {/* Top row sky-blue squares - alternating #CEECF4 and #A7DFFC */}
      <path
        d="M280.34 88.58L357.18 88.58V11.74L280.34 11.74V88.58Z"
        fill="#CEECF4"
        stroke="black"
        strokeMiterlimit="10"
      />
      <path
        d="M126.67 88.58L203.51 88.58V11.74L126.67 11.74L126.67 88.58Z"
        fill="#CEECF4"
        stroke="black"
        strokeMiterlimit="10"
      />
      <path
        d="M357.18 88.58L434.02 88.58V11.74L357.18 11.74V88.58Z"
        fill="#A7DFFC"
        stroke="black"
        strokeMiterlimit="10"
      />
      <path
        d="M203.51 88.58L280.35 88.58V11.74L203.51 11.74V88.58Z"
        fill="#A7DFFC"
        stroke="black"
        strokeMiterlimit="10"
      />

      {/* Bottom right corner - gray placeholder (no stroke) */}
      <path
        d="M577.71 242.26H510.87V165.42H587.71V232.26C587.71 237.78 583.23 242.26 577.71 242.26Z"
        fill="#C4C4C4"
      />

      {/* Top row continued */}
      <path
        d="M434.03 88.58L510.87 88.58V11.74L434.03 11.74V88.58Z"
        fill="#CEECF4"
        stroke="black"
        strokeMiterlimit="10"
      />

      {/* Bottom right corner - sky-blue with stroke */}
      <path
        d="M577.71 242.26H510.87V165.42H587.71V232.26C587.71 237.78 583.23 242.26 577.71 242.26Z"
        fill="#A7DFFC"
        stroke="black"
        strokeMiterlimit="10"
      />

      {/* Top right corner - sky-blue with stroke */}
      <path
        d="M587.71 88.58L510.87 88.58V11.74L577.71 11.74C583.23 11.74 587.71 16.22 587.71 21.74V88.58Z"
        fill="#A7DFFC"
        stroke="black"
        strokeMiterlimit="10"
      />

      {/* Middle right column - sky-blue */}
      <path
        d="M510.87 165.42H587.71V88.58L510.87 88.58V165.42Z"
        fill="#CEECF4"
        stroke="black"
        strokeMiterlimit="10"
      />

      {/* Bottom row sky-blue squares */}
      <path
        d="M280.34 242.26H357.18V165.42H280.34V242.26Z"
        fill="#CEECF4"
        stroke="black"
        strokeMiterlimit="10"
      />
      <path
        d="M126.67 242.26H203.51V165.42H126.67L126.67 242.26Z"
        fill="#CEECF4"
        stroke="black"
        strokeMiterlimit="10"
      />
      <path
        d="M357.18 242.26H434.02V165.42H357.18V242.26Z"
        fill="#A7DFFC"
        stroke="black"
        strokeMiterlimit="10"
      />

      {/* Column 6 bottom: Dark gradient */}
      <path
        d="M434.03 242.26H510.87V165.42H434.03V242.26Z"
        fill="url(#crt_paint7)"
        stroke="black"
        strokeMiterlimit="10"
      />

      {/* Column 6 bottom: White center path square */}
      <path
        d="M448.43 235.92H496.48C500.931 235.92 504.54 232.311 504.54 227.86V179.81C504.54 175.359 500.931 171.75 496.48 171.75H448.43C443.979 171.75 440.37 175.359 440.37 179.81V227.86C440.37 232.311 443.979 235.92 448.43 235.92Z"
        fill="white"
      />

      {/* Column 3 bottom: sky-blue */}
      <path
        d="M203.51 242.26H280.35V165.42H203.51V242.26Z"
        fill="#A7DFFC"
        stroke="black"
        strokeMiterlimit="10"
      />

      {/* Star decoration - shadow layer (inline absolute paths from SVG source) */}
      <path
        d="M387.07 41.4101L393.69 29.4601C394.33 28.3101 395.97 28.2601 396.67 29.3801L403.9 40.9701L417.31 43.5701C418.61 43.8201 419.15 45.3701 418.31 46.3801L409.52 56.8401L411.19 70.4001C411.35 71.7101 410.05 72.7101 408.82 72.2201L396.16 67.0901L383.78 72.8701C382.58 73.4301 381.23 72.5001 381.32 71.1801L382.29 57.5501L372.97 47.5601C372.07 46.5901 372.54 45.0201 373.82 44.7001L387.08 41.4101H387.07Z"
        fill="#303030"
      />

      {/* Star decoration - white top layer */}
      <path
        d="M387.07 40.11L393.69 28.16C394.33 27.01 395.97 26.96 396.67 28.08L403.9 39.67L417.31 42.27C418.61 42.52 419.15 44.07 418.31 45.08L409.52 55.54L411.19 69.1C411.35 70.41 410.05 71.41 408.82 70.92L396.16 65.79L383.78 71.57C382.58 72.13 381.23 71.2 381.32 69.88L382.29 56.25L372.97 46.26C372.07 45.29 372.54 43.72 373.82 43.4L387.08 40.11H387.07Z"
        fill="white"
      />

      {/* Red gradient triangle/arrow on left */}
      <path
        d="M11.3999 126.99L126.66 242.25L125.93 11.73L11.3999 126.99Z"
        fill="url(#crt_paint8)"
        stroke="black"
        strokeMiterlimit="10"
      />

      {/* Render pawns column by column from right to left for 3D overlap effect */}
      {renderMultiplePositions(
        RENDER_ORDER,
        pawns,
        CENTER_SQUARE_BOUNDS,
        undefined,
        SCALE_MULTIPLIER,
        animatingPawnIds,
        onPawnClick,
        clickablePawnIds,
        rollbackPawnIds,
        kickingPawnIds,
        pawnComponents
      )}

      <defs>
        {/* paint0: Red gradient for Column 6 center */}
        <linearGradient
          id="crt_paint0"
          x1="434.03"
          y1="126.99"
          x2="510.87"
          y2="126.99"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#CE6161" />
          <stop offset="0.48" stopColor="#C14848" />
          <stop offset="1" stopColor="#BC2A2A" />
        </linearGradient>

        {/* paint1: Dark gradient for Column 5 center */}
        <linearGradient
          id="crt_paint1"
          x1="357.19"
          y1="126.99"
          x2="434.03"
          y2="126.99"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#333333" />
          <stop offset="1" stopColor="#1A1A1A" />
        </linearGradient>

        {/* paint2: Red gradient for Column 4 center */}
        <linearGradient
          id="crt_paint2"
          x1="280.35"
          y1="126.99"
          x2="357.19"
          y2="126.99"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#CE6161" />
          <stop offset="0.48" stopColor="#C14848" />
          <stop offset="1" stopColor="#BC2A2A" />
        </linearGradient>

        {/* paint3: Dark gradient for Column 3 center */}
        <linearGradient
          id="crt_paint3"
          x1="203.5"
          y1="126.99"
          x2="280.35"
          y2="126.99"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#333333" />
          <stop offset="1" stopColor="#1A1A1A" />
        </linearGradient>

        {/* paint4: Red gradient for Column 2 center */}
        <linearGradient
          id="crt_paint4"
          x1="126.66"
          y1="126.99"
          x2="203.5"
          y2="126.99"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#CE6161" />
          <stop offset="0.48" stopColor="#C14848" />
          <stop offset="1" stopColor="#BC2A2A" />
        </linearGradient>

        {/* paint5: Dark gradient for ground border */}
        <linearGradient
          id="crt_paint5"
          x1="357.16"
          y1="257.3"
          x2="357.16"
          y2="283.98"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#333333" />
          <stop offset="1" stopColor="#1A1A1A" />
        </linearGradient>

        {/* paint6: Gray gradient for ground overlay */}
        <linearGradient
          id="crt_paint6"
          x1="357.2"
          y1="240.41"
          x2="357.2"
          y2="265.27"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#F2F2F2" />
          <stop offset="1" stopColor="#808080" />
        </linearGradient>

        {/* paint7: Dark gradient for Column 6 bottom */}
        <linearGradient
          id="crt_paint7"
          x1="434.03"
          y1="203.83"
          x2="510.87"
          y2="203.83"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#333333" />
          <stop offset="1" stopColor="#1A1A1A" />
        </linearGradient>

        {/* paint8: Red gradient for left triangle/arrow */}
        <linearGradient
          id="crt_paint8"
          x1="126.66"
          y1="126.99"
          x2="11.3999"
          y2="126.99"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#CE6161" />
          <stop offset="0.48" stopColor="#C14848" />
          <stop offset="1" stopColor="#BC2A2A" />
        </linearGradient>
      </defs>
    </svg>
  );
}
